import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import style from "../style/registration.module.scss";
import {CounselorCheckboxField} from "./CounselorCheckboxField";
import {CounselorAffiliationDTO, CounselorCheckOptions, TaxSelectedValue} from "../../../types/counselor";
import {AdminRoleCategory} from "../../../types/adminRole";
import {useCounselorGroup} from "./hooks/useCounselorGroup";
import {TaxSettings} from "./hooks/TaxSettings";
import {DistributionAmount} from "./hooks/DistributionAmount";
import {useCounselorStore} from "./hooks/counselorStore";


interface Args {
    isVatInclude: TaxSelectedValue;
    setIsVatInclude: (_: TaxSelectedValue) => void;
    isTaxDeducted: TaxSelectedValue;
    setIsTaxDeducted: (_: TaxSelectedValue) => void;
    counselorGroup: string;
    setCounselorGroup: (_: string) => void;
}

export const CounselorAffiliationSelector = React.memo((
    {
        isVatInclude,
        setIsVatInclude,
        isTaxDeducted,
        setIsTaxDeducted,
        counselorGroup,
        setCounselorGroup,
    }: Args) => {
    const {t} = useTranslation("counselorGroup");
    const {getAffiliationInfo} = useCounselorGroup();
    const [affiliationInfo, setAffiliationInfo] = useState<CounselorAffiliationDTO | null>(null);
    const isAdmin = localStorage.getItem('role') === AdminRoleCategory.ADMIN_ROOT;
    const {counselorDTO, setCounselorDTO, validates, setValidate} = useCounselorStore();

    const isManager = useMemo(() =>
            localStorage.getItem('role') === AdminRoleCategory.CONSULTANT_MANAGER,
        []
    );

    // 관리자 정보 로드
    useEffect(() => {
        const loadManagerInfo = async () => {
            if (!isManager) return;

            const result = await getAffiliationInfo();
            if (!result) return;

            setAffiliationInfo(result);
            setCounselorGroup(String(result.idx));
        };

        loadManagerInfo();
    }, [isManager, getAffiliationInfo, setCounselorGroup]);

    // 소속 정보에 따른 금액 설정
    useEffect(() => {
        if (affiliationInfo) {
            setCounselorDTO({
                ...counselorDTO,
                consultantDistributionAmount: affiliationInfo.consultantDistributionAmount,
                affiliationDistributionAmount: affiliationInfo.affiliationDistributionAmount,
                counselingAffiliationIdx: counselorGroup
            });
        } else {
            setCounselorDTO({
                ...counselorDTO,
                counselingAffiliationIdx: counselorGroup
            });
        }
        //eslint-disable-next-line
    }, [affiliationInfo, counselorGroup, setCounselorGroup]);

    return (
        <>
            {isAdmin && (
                <>
                    <span className={style.mainTitle}>{t('registration.group.title')}</span>

                    <CounselorCheckboxField
                        label={t("registration.counselor.counselorGroup")}
                        isRequired={true}
                        placeHolder={t('registration.nationalityPlaceHolder')}
                        checkboxOption={CounselorCheckOptions.COUNSELOR_GROUP}
                        option={counselorGroup}
                        setOption={setCounselorGroup}
                        validate={validates.affiliationIdx}
                        setValidate={(status) => setValidate('affiliationIdx', status)}
                        setConsultantDistributionAmount={(value) =>
                            setCounselorDTO({...counselorDTO, consultantDistributionAmount: value})}
                        setAffiliationInfo={setAffiliationInfo}
                    >

                    </CounselorCheckboxField>

                    <DistributionAmount
                        affiliationInfo={affiliationInfo}
                        affiliationAmount={counselorDTO.affiliationDistributionAmount}
                        consultantAmount={counselorDTO.consultantDistributionAmount}
                        setConsultantAmount={(value) =>
                            setCounselorDTO({...counselorDTO, consultantDistributionAmount: value})}
                        consultantValidate={validates.consultantDistribution}
                        setConsultantValidate={(status) => setValidate('consultantDistribution', status)}
                    />

                    <TaxSettings
                        affiliationInfo={affiliationInfo}
                        isVatInclude={isVatInclude}
                        setIsVatInclude={setIsVatInclude}
                        isTaxDeducted={isTaxDeducted}
                        setIsTaxDeducted={setIsTaxDeducted}
                    />
                </>
            )}
        </>
    );
});